import CreditCardIcon from '@mui/icons-material/CreditCardOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAddOutlined';
import type { ReactNode } from 'react';

import type { ActionableNeed } from './actionAppearance';
import type { OutstandingActionKind } from './rowActions';

/**
 * One icon per action kind.
 *
 * A menu of eight text-only rows makes a director read every label to find the
 * one they want. The icon is what lets them scan it — and it has to be tied to
 * the *kind*, not chosen at each call site, or the same action ends up wearing
 * two different icons on two screens.
 *
 * The split is what the action DOES, not what it is about: `pay` and `sign` act
 * on the record, everything else fetches a file. So every link action carries
 * the download icon — a director scanning the menu can tell at a glance which
 * items will hand them a document and which will change something.
 */
const ICONS: Record<OutstandingActionKind, ReactNode> = {
  pay: <CreditCardIcon fontSize="small" />,
  sign: <DrawIcon fontSize="small" />,
  invite: <GroupAddIcon fontSize="small" />,
  viewDocument: <DownloadIcon fontSize="small" />,
  viewAsicInvoice: <DownloadIcon fontSize="small" />,
  viewAgentInvoice: <DownloadIcon fontSize="small" />,
  viewReceipt: <DownloadIcon fontSize="small" />,
  viewResolution: <DownloadIcon fontSize="small" />,
};

export const actionIconOf = (kind: OutstandingActionKind): ReactNode => ICONS[kind];

/**
 * The icon for a card's own action, keyed by what the item needs.
 *
 * `signAndPay` takes the PEN, not the card: the guided flow opens on the
 * signature — the resolution authorises the charge, so it is asked for first —
 * and a credit-card icon on a button that lands on a canvas promises the wrong
 * screen. The button's colour still says money is at stake; the icon says which
 * step comes first.
 */
const NEED_ICONS: Record<ActionableNeed, ReactNode> = {
  pay: ICONS.pay,
  sign: ICONS.sign,
  signAndPay: ICONS.sign,
};

export const needIconOf = (need: ActionableNeed): ReactNode => NEED_ICONS[need];
